<template>
    <tr :class="trClasses">
        <!-- Date -->
        <td :class="rowClasses">
            <span class="fw-bold">{{ sale.formattedDisplayDate }}</span>
            <!-- Sale id -->
            <div class="text-dimmed text-small" style="font-size: 0.7em">
                #{{ sale.sale_id }}
            </div>
        </td>

        <!-- Product -->
        <td :class="rowClasses">
            <!-- Package icon -->
            <span v-if="sale.is_package" class="fas fa-box-open"></span>
            <!-- Product name -->
            <span class="fw-bold word-break">{{ sale.product_name }}</span>
            <!-- Quantity xs -->
            <span class="d-inline d-sm-none">({{ sale.quantity }})</span>
            <br class="d-block d-lg-none">
            <!-- Class name -->
            <span class="ms-1" v-if="sale.class_short_name && !sale.is_payment">({{ sale.class_short_name }})</span>
            <!-- Event time -->
            <span v-if="sale.show_time">({{ sale.display_time }})</span>
            <!-- Package label - Displayed if is package or part of package -->
            <span v-if="sale.is_package || sale.is_from_package">({{ $t('Pakke') }})</span>
            <!-- VAT -->
            <div v-if="sale.mva > 0">{{ $t('Inkl. {percentage}% mva.', { percentage: sale.mva }) }}</div>
            <!-- Location -->
            <div v-if="showLocation" class="d-none d-sm-block">{{ $t('Sted') }}: {{ sale.location }}</div>


            <!-- Products left in package -->

            <div v-if="sale.is_strict_invoicing_and_debiting">
                <div v-if="isPackage">
                    {{ $t('Gjenstående pakkebeløp') }}: {{sale.formattedRemainingPackageAmount}}
                </div>
                <div v-else-if="showTotalDiscountedAmount">
                    {{ $t('Varesalg etter rabatt') }}: <span class="no-break" v-html="formattedMoneyHtml(sale.formattedTotalAmountInclMva)"></span>
                </div>
                <div v-else-if="showTotalCorrectedAmount">
                    {{ $t('Varesalg etter korreksjon') }}: <span class="no-break" v-html="formattedMoneyHtml(sale.formattedTotalAmountInclMva)"></span>
                </div>
            </div>
            <div v-else>
                <!-- Price after discount -->
                <div v-if="isPackage">
                    {{ $t('Gjenstående i pakke') }}: {{sale.quantity}}
                </div>
                <div v-else-if="showTotalDiscountedAmount">
                    {{ $t('Varesalg etter rabatt') }}: <span class="no-break" v-html="formattedMoneyHtml(sale.formattedTotalAmountInclMva)"></span>
                </div>
                <div v-else-if="showTotalCorrectedAmount">
                    {{ $t('Varesalg etter korreksjon') }}: <span class="no-break" v-html="formattedMoneyHtml(sale.formattedTotalAmountInclMva)"></span>
                </div>
            </div>


            <!-- Package corrections -->
            <div v-for="relatedSale in sale.related_sales">
                {{ relatedSale.product_name }} (#{{relatedSale.sale_id}}) ({{relatedSale.formattedDisplayDate}})
            </div>

            <div v-if="sale.related_sale">
                {{ sale.related_sale.product_name }} (#{{sale.related_sale.sale_id}}) ({{ sale.related_sale.formattedDisplayDate }})
            </div>
        </td>
        <td :class="rowClasses" v-if="isCompanyAccount">{{ sale.student_name }}</td>
        <td :class="rowQtyClasses" class="d-none d-sm-table-cell">{{ sale.quantity }}</td>
        <td :class="rowAmountClasses" v-if="!hideSales"><div class="no-break" v-if="sale.is_payment" v-html="formattedMoneyHtml(sale.formattedTotalAmount)"></div></td>
        <td :class="rowAmountClasses">
            <div v-if="!sale.is_payment">
                <div class="no-break" v-if="sale.company_id === 0 && (sale.total_amount_incl_mva !== 0 || sale.total_discount !== 0)" v-html="formattedMoneyHtml(sale.formattedTotalAmount)"></div>
                <div class="no-break" v-else-if="sale.company_id !== 0">{{ $t('Avtale') }}</div>
                <div class="no-break" v-else-if="sale.is_from_package && sale.total_amount_incl_mva === 0">{{ $t('Pakke') }}</div>
                <div class="no-break" v-show="sale.total_discount !== 0">{{ getOperatorForAmount(sale.total_discount * -1) }}<span v-html="formattedMoneyHtml(sale.formattedTotalDiscount)"></span></div>
            </div>
        </td>
    </tr>
</template>

<script setup>
    import { computed } from 'vue';
    import Store from "../../state/accountStore";

    const package_correction_product_id = 21495;
    const props = defineProps(['sale_index', 'sale_prop', 'hideSales']);

    // Computed
    const sale = computed(() => props.sale_prop)
    const isPackage = computed(() => sale.value.is_package)
    const showLocation = computed(() => sale.value.show_location && sale.value.location.length)
    const showTotalDiscountedAmount = computed(() => sale.value.total_amount_incl_mva < sale.value.total_amount_with_discount)
    const showTotalCorrectedAmount = computed(() => sale.value.total_amount_incl_mva > sale.value.total_amount_with_discount)
    const isCompanyAccount = computed(() => Store.isCompanyAccount())

    const trClasses = computed(() => {
        const classes = {};
        if (isPackage.value) {
            classes['bg-danger-lightest'] = true;
        }
        return classes;
    })
    const rowQtyClasses = computed(() => {
        const classes = {};
        if (sale.value.is_reservation) {
            classes['fst-italic'] = true;
            classes['text-primary-lightest'] = true;
        }
        if (isPackage.value) {
            classes['bg-danger-lightest'] = true;
        }

        classes['text-center'] = true;

        return classes;
    })
    const rowAmountClasses = computed(() => {
        const classes = {
            'p-1': true,
            'p-sm-3': true,
        };
        if (sale.value.is_reservation) {
            classes['fst-italic'] = true;
            classes['text-primary-lightest'] = true;
        }
        if (isPackage.value) {
            classes['bg-danger-lightest'] = true;
        }

        classes['text-end'] = true;

        return classes;
    })
    const rowClasses = computed(() => {
        const classes = {
            'p-1': true,
            'p-sm-3': true,
        };
        if (sale.value.is_reservation) {
            classes['fst-italic'] = true;
            classes['text-primary-lightest'] = true;
        }
        if (isPackage.value) {
            classes['bg-danger-lightest'] = true;
        }
        return classes;
    })

    // Methods
    const formattedMoneyHtml = (money) => {
        return '<span class="d-none d-sm-inline">' + money + '</span><span class="d-inline d-sm-none">' +  money.replace(',00', '') + '</span>';
    }

    const getOperatorForAmount = (amount) => {
        if (amount > 0) {
            return '+';
        } else if (amount < 0) {
            return '-';
        }
        return '';
    }
</script>

<style scoped>
    .word-break {
        hyphens: auto;
        word-wrap: break-word;
    }
    .no-break {
        white-space: nowrap;
    }
</style>
